import React, { useState, useEffect } from "react";
import styled from "styled-components";
import API from "../../../util/api";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";
import { STATES } from "../../../util/constant";

// ── Styled Components ────────────────────────────────────────────────────────

const breakpoints = {
  tablet: 768,
  phone:  480,
};

const Wrapper = styled.div`
  width: 100%;

  @media (max-width: ${breakpoints.phone}px) {
    max-width: 750px;
    margin: 0 auto;
  }
`;


const FilterSection = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 1rem;
  align-items: center;

  label {
    font-size: 14px;
    color: ${({ colors }) => colors.labelText};
    margin-right: 4px;
  }

  select {
    padding: 5px 8px;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 5px;
    font-size: 14px;
  }
`;

const ControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 16px 0;
  align-items: center;

  label {
    font-size: 14px;
    margin-right: 8px;
    color: ${({ colors }) => colors.labelText};
  }

  select,
  input {
    padding: 5px 10px;
    font-size: 14px;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 5px;
  }

  input {
    width: 200px;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  ${adminTable};

  thead th {
    ${adminTableHeaderCell};
    background-color: #fff;
    position: relative;

    .sort-icons {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      flex-direction: column;
      font-size: 10px;

      span {
        cursor: pointer;
        line-height: 10px;
        color: #9CA3AF;
        display: block;
      }

      .active {
        color: #A855F7;
      }
    }
  }

  tbody td {
    ${adminTableDataCell};
  }

  a {
    color: ${({ colors }) => colors.labelText};
    font-weight: bold;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const InfoText = styled.div`
  margin: 10px 0;
  font-size: 12px;
  color: ${({ colors }) => colors.labelText};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;

  button {
    padding: 5px 10px;
    margin-left: 5px;
    border: 1px solid ${({ colors }) => colors.border};
    background-color: ${({ colors }) => colors.cardBackground};
    border-radius: 5px;
    cursor: pointer;

    &:disabled {
      background-color: ${({ colors }) => colors.disabledBackground};
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      background-color: ${({ colors }) => colors.buttonBackground};
      color: white;
    }
  }
`;

const Link = styled.a`
  color: ${({ colors }) => colors.labelText};
  font-weight: bold;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

function formatDate(iso) {
  // expects "YYYY-MM-DD"
  const [y, m, d] = iso.split("-");
  return `${m}-${d}-${y}`;
}

const SORT_KEY_MAP = {
  invoice: "invoice_number",
  org: "org_name",
  contact_name: "contact_name",
  location: "org_state",
  startDate: "start_date",
  endDate: "end_date",
  tag: "salesperson_tag",
  implemented: "implemented",
  renewed: "renewed",        
};

// ── Component ───────────────────────────────────────────────────────────────

export default function ExpiringSubscriptionsContent({ colors }) {
  // filters / pagination / sort
  const [stateFilter, setStateFilter] = useState("0");
  const [expiresIn, setExpiresIn] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState("org");
  const [sortOrder, setSortOrder] = useState("ASC");

  // data
  const [subscriptions, setSubscriptions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // fetch whenever relevant params change
  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const payload = {
          state: stateFilter === "0" ? "" : stateFilter,
          expired: 1,
          expire_in: expiresIn,
          page: currentPage - 1,
          items_per_page: pageSize,
          order_by: SORT_KEY_MAP[sortKey] || "org_name",
          order: sortOrder,
          ...(searchTerm && { search: searchTerm }),
        };
        const resp = await API.getSubscriptionListing(payload);
        setSubscriptions(resp.subscriptions || []);
        setTotal(Number(resp.total) || 0);
      } catch (err) {
        console.error("Failed to load expiring subscriptions:", err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [
    stateFilter,
    expiresIn,
    searchTerm,
    pageSize,
    currentPage,
    sortKey,
    sortOrder,
  ]);

  const pageCount = Math.ceil(total / pageSize);

  const handleSort = (key, order) => {
    setSortKey(key);
    setSortOrder(order.toUpperCase());
    setCurrentPage(1);
  };

  return (
    <Wrapper>
      <FilterSection colors={colors}>
        <div>
          <label>State:</label>
          <select
            value={stateFilter}
            onChange={(e) => {
              setStateFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="0">All</option>
            {Object.entries(STATES).map(([name, abbr]) => (
              <option key={abbr} value={abbr}>{name}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Expires in:</label>
          <select
            value={expiresIn}
            onChange={(e) => {
              setExpiresIn(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={1}>1 Month</option>
            <option value={3}>3 Months</option>
            <option value={6}>6 Months</option>
          </select>
        </div>
      </FilterSection>

      <ControlsContainer colors={colors}>
        <div>
          <label>Show:</label>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <label style={{ marginLeft: 8 }}>entries</label>
        </div>
        <div>
          <label>Search:</label>
          <input
            type="text"
            placeholder="Search invoice or org…"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
      </ControlsContainer>

      {loading ? (
        <p>Loading…</p>
      ) : (
        <>
          <TableWrapper>
            <Table colors={colors}>
              <thead>
                <tr>
                  {[
                    { key: "invoice", label: "Invoice" },
                    { key: "org", label: "Org" },
                    { key: "contact_name", label: "Contact" },
                    { key: "location", label: "Location" },
                    { key: "startDate", label: "Start Date" },
                    { key: "endDate", label: "End Date" },
                    { key: "tag", label: "Tag" },
                    { key: "implemented", label: "Implemented" },
                    { key: "renewed", label: "Renewed" },
                  ].map(({ key, label }) => {
                    const isAscActive = sortKey === key && sortOrder === "ASC";
                    const isDescActive = sortKey === key && sortOrder === "DESC";
                    const defaultColor = "#9CA3AF";
                    const activeColor = "#A855F7";
                    return (
                      <th key={key}>
                        {label}
                        <div className="sort-icons">
                          <span
                            onClick={() => handleSort(key, "asc")}
                            className={isAscActive ? "active" : ""}
                            style={{
                              color: isAscActive ? activeColor : defaultColor,
                              display: isDescActive ? "none" : "block"
                            }}
                          >
                            ▲
                          </span>
                          <span
                            onClick={() => handleSort(key, "desc")}
                            className={isDescActive ? "active" : ""}
                            style={{
                              color: isDescActive ? activeColor : defaultColor,
                              display: isAscActive ? "none" : "block"
                            }}
                          >
                            ▼
                          </span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((row) => (
                  <tr key={row.invoice_id}>
                    <td>
                      <Link
                        href={`/admincentral/invoices/details/${row.invoice_id}`}
                        colors={colors}
                      >
                        {row.invoice_number}
                      </Link>
                      <br />
                      PD: {formatDate(row.purchase_date)}
                    </td>
                    <td>
                      <Link
                        href={`/admincentral/become/${row.customer_id}`}
                        colors={colors}
                      >
                        {row.org_name}
                      </Link>
                    </td>
                    <td>
                      {row.contact_name}
                      <br />
                      <i>{row.contact_email}</i>
                      <br />
                      {row.contact_phone || ""}
                    </td>
                    <td>
                      {row.org_city}, {row.org_state} {row.org_zip}
                    </td>
                    <td>{formatDate(row.start_date)}</td>
                    <td>{formatDate(row.end_date)}</td>
                    <td>{row.salesperson_tag}</td>
                    <td>{row.implementation || ""}</td>
                    <td>{row.renewed || ""}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>

          <InfoText colors={colors}>
            Showing {(currentPage - 1) * pageSize + 1} to{" "}
            {Math.min(currentPage * pageSize, total)} of {total} entries
          </InfoText>
          <Pagination colors={colors}>
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, Math.ceil(total / pageSize)))}
              disabled={currentPage === Math.ceil(total / pageSize)}
            >
              Next
            </button>
          </Pagination>
        </>
      )}
    </Wrapper>
  );
}
